import { FC, useMemo, SetStateAction, Dispatch } from 'react';

import useLocale from '@/hooks/useLocale';

import { TypeSubEngineInstallment } from '@/lib/api/subscriptionManager/types';

import { DisplayDateFormat } from '@/utils/dates/constants';
import { formatDateForDisplay } from '@/utils/dates/dateFormatters';

import { TypeAccountERCTranslations } from '@/utils/translations/types/TypeAccountERCTranslations';

import EditShipDate from './EditShipDate';
import classNames from 'classnames';

export type ShipDateProps = {
  t: TypeAccountERCTranslations;
  installment: TypeSubEngineInstallment;
  setInstallment?: Dispatch<SetStateAction<TypeSubEngineInstallment>>;
  isSkippedBox?: boolean;
  isPausedBox?: boolean;
};

const ShipDate: FC<ShipDateProps> = ({
  t,
  installment,
  setInstallment,
  isSkippedBox,
  isPausedBox,
}) => {
  const { scheduled_date: scheduledDate } = installment;

  const locale = useLocale();

  const shippingDate = useMemo(
    () =>
      formatDateForDisplay(
        scheduledDate,
        locale,
        DisplayDateFormat.ShortMonthDayYear,
      ),
    [scheduledDate, locale],
  );

  const canEditShipDate = !!setInstallment && !isSkippedBox;

  return (
    <div
      className="flex flex-row gap-x-2 md:gap-x-4 items-end"
      data-testid="shipDate"
    >
      <p
        className={classNames('text-sm md:text-base flex flex-row gap-2', {
          'text-grey-midtone': isSkippedBox,
        })}
      >
        <span className="font-bold">
          {isPausedBox ? t.resumeDate : t.shipDate}
        </span>
        <span>{isSkippedBox ? t.skippedBoxShipDate : shippingDate}</span>
      </p>

      {canEditShipDate && (
        <EditShipDate
          t={t}
          installment={installment}
          setInstallment={setInstallment}
        />
      )}
    </div>
  );
};

export default ShipDate;
